import {
  getPosthogDistinctId,
  getPosthogSessionId,
  trackEvent,
} from '@/lib/analytics'
import {
  pendingSavedPlanEventProperties,
  readPendingSavedPlanFlow,
  type SavedPlanAnalyticsContext,
} from '@/lib/saved-plan-analytics'

const CLAIMED_ANALYSIS_PREFIX = 'claimedAnalysis_'
const claimedAnalysisInMemory = new Set<string>()

export function justAuthedCookieFresh(maxAgeMs: number): boolean {
  try {
    const match = document.cookie.match(/(?:^|;\s*)just_authed=(\d+)/)
    if (!match) return false
    const ts = Number(match[1])
    return ts > 0 && Date.now() - ts < maxAgeMs
  } catch {
    return false
  }
}

export function wasJustAuthed(maxAgeMs = 180_000): boolean {
  if (typeof window === 'undefined') return false

  // The OAuth callback exchanges the code server-side, so the destination page
  // loads with the session already present and the client-side SIGNED_IN
  // (null → session) transition that sets the sessionStorage sentinel never
  // happens. The callback route drops a short-lived `just_authed` cookie for
  // exactly this case — accept either signal.
  if (justAuthedCookieFresh(maxAgeMs)) return true

  let allowFlowRestore = false
  let sentinelStillValid = false
  try {
    const sentinel = window.sessionStorage.getItem('justAuthed')
    const consumed = window.sessionStorage.getItem('justAuthedConsumed') === '1'
    const ts = sentinel ? Number(sentinel) : 0
    sentinelStillValid = ts > 0 && Date.now() - ts < maxAgeMs
    allowFlowRestore = sentinelStillValid && !consumed
    if (sentinelStillValid && consumed) {
      window.sessionStorage.removeItem('justAuthed')
      window.sessionStorage.removeItem('justAuthedConsumed')
    } else if (!sentinelStillValid) {
      window.sessionStorage.removeItem('justAuthed')
      window.sessionStorage.removeItem('justAuthedConsumed')
    }
  } catch {
    // sessionStorage unavailable
  }

  return allowFlowRestore
}

export function claimAnalysisOnce(
  analysisId: string | null | undefined,
  source: string,
  options?: {
    resultAccessToken?: string
    analyticsContext?: SavedPlanAnalyticsContext
    onSuccess?: () => void
  },
): void {
  if (!analysisId || !options?.resultAccessToken) return
  if (typeof window === 'undefined') return

  const key = `${CLAIMED_ANALYSIS_PREFIX}${analysisId}`
  if (claimedAnalysisInMemory.has(key)) return

  try {
    if (window.sessionStorage.getItem(key)) return
    window.sessionStorage.setItem(key, '1')
  } catch {
    // Keep the in-memory guard below as a best-effort fallback.
  }
  claimedAnalysisInMemory.add(key)

  const pendingFlow = readPendingSavedPlanFlow(analysisId)
  const analyticsContext = options?.analyticsContext ?? pendingFlow ?? null
  const eventProperties = (extra: Record<string, string | number | boolean | undefined>) => ({
    ...(analyticsContext
      ? pendingSavedPlanEventProperties({ ...analyticsContext, analysisId }, extra)
      : { analysis_id: analysisId, ...extra }),
    source,
  })
  const releaseRetryGuard = () => {
    claimedAnalysisInMemory.delete(key)
    try {
      window.sessionStorage.removeItem(key)
    } catch {
      // The in-memory guard has already been released.
    }
  }

  const distinctId = getPosthogDistinctId()
  const sessionId = getPosthogSessionId()
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (distinctId) headers['X-POSTHOG-DISTINCT-ID'] = distinctId
  if (sessionId) headers['X-POSTHOG-SESSION-ID'] = sessionId
  headers['X-SAVED-PLAN-ANALYSIS-ID'] = analysisId
  if (analyticsContext) {
    headers['X-SAVED-PLAN-MODE'] = analyticsContext.mode
    headers['X-SAVED-PLAN-TYPE'] = analyticsContext.planType
  }

  void fetch('/api/user/claim-analysis', {
    method: 'POST',
    headers,
    body: JSON.stringify({
      analysisId,
      ...(options?.resultAccessToken ? { resultAccessToken: options.resultAccessToken } : {}),
      ...(analyticsContext ? {
        analytics: {
          analysisId,
          mode: analyticsContext.mode,
          planType: analyticsContext.planType,
          outcome: analyticsContext.outcome,
          entryPath: analyticsContext.entryPath,
          funnelId: analyticsContext.funnelId,
          subject: analyticsContext.subject,
          saveFlowId: pendingFlow?.saveFlowId,
          method: pendingFlow?.method,
          provider: pendingFlow?.provider,
        },
      } : {}),
    }),
  })
    .then(async (response) => {
      const data = await response.json().catch(() => ({}))
      const claimed = Boolean(data?.claimed)
      const requestOutcome = response.ok
        ? claimed
          ? 'claimed'
          : data?.planFinalizationQueued
            ? 'already_owned'
            : 'not_claimed'
        : 'http_error'
      trackEvent('analysis_claimed', eventProperties({
        request_outcome: requestOutcome,
        http_status: response.status,
        claimed: Boolean(data?.claimed),
        plan_finalization_queued: Boolean(data?.planFinalizationQueued),
        analysis_type: typeof data?.analysisType === 'string' ? data.analysisType : undefined,
        failure_reason: response.ok
          ? undefined
          : typeof data?.error === 'string' ? data.error.slice(0, 160) : 'request_failed',
      }))
      if (!response.ok) {
        releaseRetryGuard()
      } else {
        try {
          options.onSuccess?.()
        } catch {
          // Claim succeeded; URL/history cleanup is best-effort only.
        }
      }
    })
    .catch((error) => {
      trackEvent('analysis_claimed', eventProperties({
        request_outcome: 'network_error',
        claimed: false,
        failure_reason: error instanceof Error ? error.message.slice(0, 160) : 'network_error',
      }))
      releaseRetryGuard()
    })
}
